import type { ReactNode } from 'react';
import { IconButton, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';

/**
 * What every state of the template panel shares (DX-650): the head with the
 * template's name and where the reader is, a body that scrolls, and a foot with
 * the panel's buttons — laid out as the request panel on Access is, so the two
 * panels read as one kind of thing.
 */
export const usePanelStyles = makeStyles(theme => ({
  paper: {
    width: 'min(480px, 100vw)',
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--ui-bg-elevated)',
  },
  head: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 2.5),
    borderBottom: '1px solid var(--ui-border)',
  },
  headText: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  title: { fontSize: '1.125rem', fontWeight: 600, margin: 0 },
  status: { margin: 0, fontSize: '0.875rem', color: 'var(--ui-text-muted)' },
  stopped: { color: 'var(--ui-danger-text)' },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(3, 2.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  foot: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(2, 2.5),
    borderTop: '1px solid var(--ui-border)',
  },
  steps: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.75, 1),
    fontSize: '0.8125rem',
    color: 'var(--ui-text-muted)',
    '& > li': { display: 'flex', alignItems: 'center', gap: 6 },
    '& > li + li::before': {
      content: '""',
      width: 20,
      height: 1,
      marginRight: 2,
      background: 'var(--color-brand-200)',
    },
  },
  current: { color: 'var(--ui-text)', fontWeight: 500 },
  mark: {
    display: 'grid',
    placeItems: 'center',
    width: 20,
    height: 20,
    boxSizing: 'border-box',
    borderRadius: '50%',
    border: '1px solid var(--color-brand-300)',
    fontSize: '0.75rem',
    lineHeight: 1,
  },
  markCurrent: {
    border: 0,
    background: 'var(--ui-primary)',
    color: '#ffffff',
    fontWeight: 700,
  },
  markDone: {
    border: 0,
    background: 'var(--ui-nav-active-bg)',
    color: 'var(--ui-primary)',
    '& svg': { fontSize: 14 },
  },
  alert: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: '1px solid var(--ui-danger-border)',
    background: 'var(--ui-danger-bg)',
    color: 'var(--ui-text)',
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
}));

type ShellProps = {
  titleId: string;
  title: string;
  /** Under the title: the steps, or how the run stands. */
  status?: ReactNode;
  foot: ReactNode;
  onClose: () => void;
  children: ReactNode;
};

export const PanelShell = ({
  titleId,
  title,
  status,
  foot,
  onClose,
  children,
}: ShellProps) => {
  const classes = usePanelStyles();
  return (
    <>
      <div className={classes.head}>
        <div className={classes.headText}>
          <h2 id={titleId} className={classes.title}>
            {title}
          </h2>
          {status}
        </div>
        <IconButton aria-label="Close" size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      <div className={classes.body}>{children}</div>
      <div className={classes.foot}>{foot}</div>
    </>
  );
};

/** One mark per page of the template's form, then Review. */
export const StepIndicator = ({
  labels,
  current,
}: {
  labels: string[];
  current: number;
}) => {
  const classes = usePanelStyles();
  return (
    <ol aria-label="Steps" className={classes.steps}>
      {labels.map((label, index) => {
        let mark = classes.mark;
        if (index === current) mark = `${classes.mark} ${classes.markCurrent}`;
        if (index < current) mark = `${classes.mark} ${classes.markDone}`;
        return (
          <li
            key={`${index}-${label}`}
            aria-current={index === current ? 'step' : undefined}
            className={index === current ? classes.current : undefined}
          >
            <span className={mark} aria-hidden>
              {index < current ? <CheckIcon /> : index + 1}
            </span>
            {label}
          </li>
        );
      })}
    </ol>
  );
};
